import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import type { BlogPostItem } from '../api/types';

const CATEGORIES = [
  { value: 'all', label: 'All Posts' },
  { value: 'tutorials', label: 'Tutorials' },
  { value: 'inspiration', label: 'Inspiration' },
  { value: 'scripture-study', label: 'Scripture Study' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'community', label: 'Community' },
];

// Placeholder content to match experiments vibe
const PLACEHOLDER_POSTS: (BlogPostItem & { content?: string; published?: boolean })[] = [
  {
    id: '1',
    title: 'Getting Started with Bible Journaling',
    excerpt: 'Simple, practical ways to begin your journaling journey with Scripture at the center.',
    category: 'tutorials',
    createdDate: new Date().toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=800&h=600&fit=crop',
    published: true,
  },
  {
    id: '2',
    title: 'Finding Inspiration in the Psalms',
    excerpt: 'Draw creative themes from the Psalms and turn them into meaningful pages.',
    category: 'inspiration',
    createdDate: new Date(Date.now() - 86400000 * 7).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1513519245088-0e12902e35a6?w=800&h=600&fit=crop',
    published: true,
  },
  {
    id: '3',
    title: 'Study Notes: Ephesians 2',
    excerpt: 'Grace, faith, and how to reflect the gospel in your creative practice.',
    category: 'scripture-study',
    createdDate: new Date(Date.now() - 86400000 * 20).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&h=600&fit=crop',
    published: true,
  },
  {
    id: '4',
    title: 'Favorite Supplies We Love',
    excerpt: 'A curated list of tools and materials that keep things meaningful, not cluttered.',
    category: 'supplies',
    createdDate: new Date(Date.now() - 86400000 * 30).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=800&h=600&fit=crop',
    published: true,
  },
  {
    id: '5',
    title: 'Community Highlight: Testimony & Art',
    excerpt: 'Stories from women integrating worship and art in everyday life.',
    category: 'community',
    createdDate: new Date(Date.now() - 86400000 * 45).toISOString(),
    imageUrl: 'https://images.unsplash.com/photo-1505682634904-d7c075b3d58b?w=800&h=600&fit=crop',
    published: true,
  },
];

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const posts = PLACEHOLDER_POSTS;
  const filteredPosts = useMemo(() => {
    return posts
      .filter(p => p.published)
      .filter(p => selectedCategory === 'all' || p.category === selectedCategory)
      .filter(p => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;
        return (
          (p.title || '').toLowerCase().includes(q) ||
          (p.excerpt || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => (b.createdDate || '').localeCompare(a.createdDate || ''));
  }, [posts, selectedCategory, searchQuery]);

  const featuredPost = filteredPosts[0];
  const remainingPosts = filteredPosts.slice(1);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#D4A5A5]/20 to-[#FDF8F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[#3D3D3D] mb-4">Blog</h1>
            <p className="font-sans text-lg text-[#3D3D3D]/70 max-w-2xl mx-auto">
              Tutorials, inspiration, and creative ideas for your Bible journaling journey
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-3 rounded-full border border-gray-200 font-sans w-full focus:border-[#D4A5A5] focus:outline-none"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`rounded-full font-sans text-sm px-4 py-2 transition-colors ${
                  selectedCategory === category.value
                    ? 'bg-[#3D3D3D] text-white'
                    : 'border border-gray-200 text-[#3D3D3D] hover:border-[#D4A5A5] hover:text-[#D4A5A5]'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#3D3D3D] mb-2">No posts found</h3>
            <p className="font-sans text-[#3D3D3D]/60">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-12">
                <Link to={`/blog-post?id=${featuredPost.id}`}>
                  <article className="group grid md:grid-cols-2 gap-8 bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                    <div className="relative overflow-hidden">
                      <img
                        src={featuredPost.imageUrl || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=800&h=600&fit=crop'}
                        alt={featuredPost.title}
                        className="w-full h-80 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {featuredPost.category && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm text-[#3D3D3D] px-3 py-1 rounded-full font-sans text-xs font-medium capitalize">
                            {featuredPost.category.replace('-', ' ')}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 text-[#3D3D3D]/50 font-sans text-sm mb-4">
                        <Clock className="w-4 h-4" />
                        <span>{featuredPost.createdDate && new Date(featuredPost.createdDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <h2 className="font-serif text-3xl font-semibold text-[#3D3D3D] mb-4 group-hover:text-[#D4A5A5] transition-colors">{featuredPost.title}</h2>
                      {featuredPost.excerpt && (
                        <p className="font-sans text-[#3D3D3D]/70 leading-relaxed mb-6">{featuredPost.excerpt}</p>
                      )}
                      <span className="inline-flex items-center gap-2 font-sans font-medium text-[#D4A5A5] group-hover:gap-3 transition-all">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </article>
                </Link>
              </motion.div>
            )}

            {/* Posts Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {remainingPosts.map((post, index) => (
                <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }}>
                  <Link to={`/blog-post?id=${post.id}`}>
                    <article className="group">
                      <div className="relative overflow-hidden rounded-2xl mb-5">
                        <img
                          src={post.imageUrl || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&h=400&fit=crop'}
                          alt={post.title}
                          className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {post.category && (
                          <div className="absolute top-4 left-4">
                            <span className="bg-white/90 backdrop-blur-sm text-[#3D3D3D] px-3 py-1 rounded-full font-sans text-xs font-medium capitalize">
                              {post.category.replace('-', ' ')}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-[#3D3D3D]/50 font-sans text-sm mb-3">
                        <Clock className="w-4 h-4" />
                        <span>{post.createdDate && new Date(post.createdDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>

                      <h3 className="font-serif text-xl font-semibold text-[#3D3D3D] mb-3 group-hover:text-[#D4A5A5] transition-colors line-clamp-2">{post.title}</h3>

                      {post.excerpt && (
                        <p className="font-sans text-[#3D3D3D]/70 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                      )}

                      <span className="inline-flex items-center gap-2 font-sans text-sm font-medium text-[#D4A5A5] group-hover:gap-3 transition-all">
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </article>
                  </Link>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;